import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { take, call } from 'redux-saga/effects';
import { calcPrice } from '@grandvisionhq/analytics';
import { MediaQuery } from '@grandvisionhq/common';
import { Actions, Configurator } from '@grandvisionhq/configurator';
import { useSelector, useSaga, actions } from '@grandvisionhq/state';
import { UploadProvider } from '@grandvisionhq/upload';
import { stepsMap as stepsMap$1 } from '../state/configurator/steps/rx-frames/index.js';
import { stepsMap } from '../state/configurator/steps/rx-sun/index.js';
import { stepSequence } from '../state/configurator/steps/shared/index.js';
import { pushDataLayerEvent } from '../state/data-layer/events.js';
import { listenForConfigurationChanges } from '../state/data-layer/sagas.js';
import { RxGlassesConfiguratorProvider } from './Context/RxGlassesConfiguratorContext.js';
import { RxGlassesTopBar } from './Footer/components/RxGlassesTopBar/RxGlassesTopBar.js';
import { useConfiguratorStepSubmit } from './hooks/use-configurator-step-submit.js';
import { usePrescriptionTypes } from './hooks/use-prescription-types.js';
import { RxGlassesExpanderContentContainer } from './Footer/components/RxGlassesExpanderContent/RxGlassesExpanderContentContainer.js';
import { useProductDetailContext } from '../../../ProductDetail/Context/ProductDetailContext.js';
import { getSelectedLensType, getSelectedPackage, getSelectedAddOn, getPrescriptionType, getBundlePrice } from '../state/configurator/selectors.js';
import { RX_SCOPE, PD_SCOPE } from '../state/constants.js';

const scopes = [
    RX_SCOPE,
    PD_SCOPE
];
const contentProps = {
    footerTopBarContent: /*#__PURE__*/ React.createElement(RxGlassesTopBar, null),
    footerExpanderContent: /*#__PURE__*/ React.createElement(RxGlassesExpanderContentContainer, null)
};
const RxGlassesConfigurator = (props)=>{
    const isDesktop = MediaQuery.useIsDesktop();
    const { routes  } = useProductDetailContext();
    const selectedLensType = useSelector((s)=>getSelectedLensType(s)
    );
    const selectedLensPackage = useSelector((s)=>getSelectedPackage(s)
    );
    const selectedLensAddOn = useSelector((s)=>getSelectedAddOn(s)
    );
    const prescriptionType = useSelector((s)=>getPrescriptionType(s)
    );
    const bundlePrice = useSelector((s)=>getBundlePrice(s, {
            id: props.id
        })
    );
    const prescriptionTypes = usePrescriptionTypes();
    const shouldCheckUploadConfig = prescriptionTypes.includes('PrescriptionFile');
    React.useEffect(()=>{
        bundlePrice && props.onPriceChange(bundlePrice);
    }, [
        bundlePrice,
        props.onPriceChange
    ]);
    useSaga(React.useMemo(()=>{
        return function*() {
            while(true){
                var ref;
                yield take((a)=>actions.isType(a, Actions.finishSuccess) && a.payload.id === props.id
                );
                if (props.onModalClose) {
                    yield call(props.onModalClose);
                }
                if ((ref = props.triggerButtonRef) === null || ref === void 0 ? void 0 : ref.current) {
                    yield call([
                        props.triggerButtonRef.current,
                        'focus'
                    ]);
                }
            }
        };
    }, [
        props.onModalClose,
        props.triggerButtonRef
    ]));
    useSaga(React.useMemo(()=>function*() {
            yield* listenForConfigurationChanges(props.id);
        }
    , [
        props.id
    ]));
    const finishHandler = useConfiguratorStepSubmit(props.id);
    /**
   * If the configurator isn't open, the contents of this entire component don't need to be rendered.
   */ if (!props.isOpen) {
        return null;
    }
    const resolvedStepsMap = props.id === 'rxSun' ? stepsMap : stepsMap$1;
    var ref1;
    const rxConfigData = {
        lens_type: selectedLensType === null || selectedLensType === void 0 ? void 0 : selectedLensType.title,
        lens_variant: selectedLensType === null || selectedLensType === void 0 ? void 0 : selectedLensType.colorCode,
        lens_package: selectedLensPackage === null || selectedLensPackage === void 0 ? void 0 : selectedLensPackage.title,
        'add-ons': selectedLensAddOn === null || selectedLensAddOn === void 0 ? void 0 : selectedLensAddOn.title,
        price: (ref1 = calcPrice(bundlePrice === null || bundlePrice === void 0 ? void 0 : bundlePrice.discountedPrice, true)) !== null && ref1 !== void 0 ? ref1 : calcPrice(bundlePrice === null || bundlePrice === void 0 ? void 0 : bundlePrice.originalPrice, true),
        prescription_input_method: prescriptionType
    };
    return /*#__PURE__*/ React.createElement(RxGlassesConfiguratorProvider, {
        frame: props.frame,
        frameSku: props.frameSku,
        opticianForm: props.opticianForm,
        configuratorDocument: props.configuratorDocument,
        id: props.id,
        usps: props.usps,
        routes: routes
    }, /*#__PURE__*/ React.createElement(UploadProvider, {
        scopes: scopes,
        shouldFetchConfigs: shouldCheckUploadConfig
    }, /*#__PURE__*/ React.createElement(Configurator, swcHelpers.extends({
        id: props.id,
        className: "glasses-configurator-modal",
        preventVerticalScroll: true,
        "data-t": "glasses-configurator-modal",
        stepSequence: stepSequence,
        stepsMap: resolvedStepsMap,
        lastStepCTALabelKey: "products.glasses.configurator.addToCart",
        isOpen: props.isOpen,
        onModalClose: props.onModalClose,
        onFinish: finishHandler
    }, isDesktop ? {} : contentProps, {
        onTriggerDataLayer: (dataInput)=>pushDataLayerEvent(swcHelpers.objectSpread({}, dataInput, {
                rx_config: rxConfigData
            }))
    }))));
};

export { RxGlassesConfigurator, RxGlassesConfigurator as default };
//# sourceMappingURL=RxGlassesConfigurator.js.map
